import React, { useContext, useState } from 'react'
import PropTypes from 'prop-types'
import { MEDIA, COLORS } from '../../utils/styles'
import styled from 'styled-components/macro'
import { DetectDeviceContext } from 'wsc/components/context/DetectDeviceProvider'
import { ReactComponent as BoyIcon } from '../../statics/images/babyname/icon-boy.svg'
import { ReactComponent as GirlIcon } from '../../statics/images/babyname/icon-girl.svg'
import { ReactComponent as IconNext } from '../../statics/images/icon-next.svg'
import Link from 'wsc/components/Link'
import { SEX_CONFIGS } from '../../configs/babyname'
import routes from '../../configs/routes'
import { numberFormat } from 'wsc/utils/number'

const ScButtonWrapper = styled(
  ({ isHovering, isHidden, height, backgroundColor, borderColor, ...restProps }) => (
    <Link {...restProps} />
  )
)`
  position: relative;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  align-items: center;
  width: 100%;
  -webkit-tap-highlight-color: transparent;

  ${MEDIA.DESKTOP`
    ${props =>
      props.isHovering &&
      `
      cursor: pointer;
    `}
  `}

  ${props =>
    props.isHidden &&
    `
    display: none;
  `}

  ${props =>
    props.height &&
    `
    height: ${props.height};
  `}

  ${props =>
    props.backgroundColor &&
    `
    background-color: ${props.backgroundColor};
  `}

  ${props =>
    props.borderColor &&
    `
    border: solid 1px ${props.borderColor};
  `}
`
const ScHeadBar = styled.div`
  width: 10px;
  height: 100%;
  margin-right: 16px;
  ${props =>
    props.defaultStateColor &&
    `
    background-color: ${props.defaultStateColor};
  `}

  ${MEDIA.DESKTOP` 
    ${props =>
      props.isHovering &&
      `
      background-color: ${props.activeStateColor};
    `}
  `}

  ${props =>
    props.isActive &&
    `
    background-color: ${props.activeStateColor};
  `}
`
const ScBoyIcon = styled(BoyIcon)`
  height: 40px;
  width: 40px;
  margin-right: 12px;
`
const ScGirlIcon = styled(GirlIcon)`
  height: 40px;
  width: 40px;
  margin-right: 12px;
`
const ScFlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
`
const ScNameFlex = styled.div`
  display: flex;
  align-items: center;
`
const ScRank = styled.p`
  align-self: flex-start;
  margin-right: 16px;
  font-size: 1.12rem;
  line-height: 1.72;
  ${MEDIA.DESKTOP`
    font-size: 1.25rem;
    line-height: 1.7;
  `}
`
const ScName = styled.p`
  font-size: 1.12rem;
  line-height: 1.72;
  margin-right: 8px;
  ${MEDIA.DESKTOP`
    font-size: 1.25rem;
    line-height: 1.7;
    margin-right: 10px;
  `}

  ${props =>
    props.fontWeight &&
    `
    font-weight: ${props.fontWeight};
  `}
`
const ScBirths = styled.div`
  color: ${COLORS.GREY}
  font-size: 0.93rem;
  line-height: 1.73;
  ${MEDIA.MOBILE`
    font-size: 1rem;
    line-height: 1.69;
  `}
  padding-left: 36px;
`
const ScNeutralGender = styled.p`
  font-size: 0.68rem;
  font-weight: 600;
  line-height: 1.73;
  letter-spacing: 0.66px;
  color: ${COLORS.PALE_ORANGE};
`
const ScIconNext = styled(
  ({ isHovering, isActive, defaultStateColor, activeStateColor, ...props }) => (
    <IconNext {...props} />
  )
)`
  position: absolute;
  height: 16px;
  width: 16px;
  right: 16px;
  ${props =>
    props.defaultStateColor &&
    `
    fill: ${props.defaultStateColor};
  `}

  ${MEDIA.DESKTOP` 
    ${props =>
      props.isHovering &&
      `
      fill: ${props.activeStateColor};
    `}
  `}

  ${props =>
    props.isActive &&
    `
    fill: ${props.activeStateColor};
  `}
`

const NameButton = ({
  id,
  name,
  nameFontWeight,
  sex,
  isHidden,
  height,
  backgroundColor,
  borderColor,
  defaultStateColor,
  activeStateColor,
  isShowGenderIcon,
  isGenderNeutral,
  rank,
  births,
}) => {
  const { isDesktop } = useContext(DetectDeviceContext)
  const [isHovering, setIsHovering] = useState(false)
  const [isActive, setIsActive] = useState(false)
  const handleMouseOver = () => {
    if (isDesktop) setIsHovering(true)
  }
  const handleMouseOut = () => {
    setIsHovering(false)
  }
  const hoverStateProps = {
    onMouseOver: handleMouseOver,
    onMouseOut: handleMouseOut,
    onTouchStart: () => {
      setIsActive(true)
    },
    onTouchEnd: () => {
      setIsActive(false)
    },
    isHovering: isHovering,
  }

  return (
    <ScButtonWrapper
      {...hoverStateProps}
      isHidden={isHidden}
      to={routes.babyNameIndividual.pathResolver(SEX_CONFIGS[sex].slug, id, name)}
      withDefaultStyle={false}
      height={height}
      backgroundColor={backgroundColor}
      borderColor={borderColor}
    >
      <ScHeadBar
        isHovering={isHovering}
        isActive={isActive}
        defaultStateColor={defaultStateColor}
        activeStateColor={activeStateColor}
      />
      {isShowGenderIcon && (sex === 'M' ? <ScBoyIcon /> : <ScGirlIcon />)}
      <ScFlexWrapper>
        <ScNameFlex>
          {rank && <ScRank>{`#${rank}`}</ScRank>}
          <ScName fontWeight={nameFontWeight}>{name}</ScName>
          {isGenderNeutral && <ScNeutralGender>Gender Neutral</ScNeutralGender>}
        </ScNameFlex>
        {!!births && <ScBirths>{`${numberFormat(births)} births`}</ScBirths>}
      </ScFlexWrapper>
      <ScIconNext
        isHovering={isHovering}
        isActive={isActive}
        defaultStateColor={defaultStateColor}
        activeStateColor={activeStateColor}
      />
    </ScButtonWrapper>
  )
}

NameButton.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  nameFontWeight: PropTypes.string,
  sex: PropTypes.string.isRequired,
  defaultStateColor: PropTypes.string.isRequired,
  activeStateColor: PropTypes.string.isRequired,
  isGenderNeutral: PropTypes.bool,
  isHidden: PropTypes.bool,
  height: PropTypes.string,
  backgroundColor: PropTypes.string,
  borderColor: PropTypes.string,
  isShowGenderIcon: PropTypes.bool,
  rank: PropTypes.number,
  births: PropTypes.number,
}

NameButton.defaultProps = {
  nameFontWeight: 'normal',
  isGenderNeutral: null,
  isHidden: false,
  height: '64px',
  backgroundColor: COLORS.WHITE,
  borderColor: COLORS.BORDER_GREY,
  isShowGenderIcon: true,
  rank: null,
  births: null,
}

export default NameButton
